import { useCallback, useEffect, useRef, useState } from 'react';
import { DEFAULT_LIMIT, fetchBlogs } from '../../api/blogs';
import BlogCard from '../common/BlogCard';
import CardSkeleton from '../common/CardSkeleton';

/*
 * How many pages load themselves on scroll before the reader has to ask for
 * more. Endless auto-loading makes the footer unreachable — every scroll toward
 * it appends another screen of cards — so after this many pages the sentinel
 * stops firing and the button comes back.
 */
const AUTO_PAGES = 3;

/**
 * Cursor-paged grid of posts, optionally filtered by category and/or search.
 *
 * Pages arrive by SCROLL: an IntersectionObserver watches a sentinel below the
 * grid and fetches the next page as it comes into view, so reading straight down
 * never stops at a button. Still cursor-paged, not page-numbered — the API
 * exposes no total count (see the contract note in src/api/blogs.js), so there
 * is no "page 4 of 12" to render and no way to jump to the last page.
 *
 * @param {string}   category      exact display name, or 'All'
 * @param {string}   query         free-text search (title only, server-side)
 * @param {number}   limit         page size
 * @param {string}   emptyMessage
 * @param {function} onLoaded      called with the first page's posts, so the
 *                                 parent can emit ItemList structured data
 */
function BlogList({
  category = 'All',
  query = '',
  limit = DEFAULT_LIMIT,
  emptyMessage = 'No articles yet.',
  onLoaded,
}) {
  const [blogs, setBlogs] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  // Pages fetched by the observer, not counting the first page or button clicks.
  const [autoLoaded, setAutoLoaded] = useState(0);

  // Guards against a slow response for a previous filter overwriting a newer
  // one (click two categories quickly and the first can land last).
  const requestId = useRef(0);
  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;

  const load = useCallback(
    async (nextCursor, replace) => {
      const id = ++requestId.current;
      replace ? setLoading(true) : setLoadingMore(true);
      setError(null);
      try {
        const data = await fetchBlogs({ category, query, limit, cursor: nextCursor });
        if (id !== requestId.current) return;
        setBlogs((prev) => (replace ? data.blogs : [...prev, ...data.blogs]));
        setCursor(data.cursor);
        setHasMore(data.hasMore);
        if (replace && onLoadedRef.current) onLoadedRef.current(data.blogs);
      } catch (err) {
        if (id === requestId.current) setError(err.message || 'Something went wrong.');
      } finally {
        if (id === requestId.current) {
          replace ? setLoading(false) : setLoadingMore(false);
        }
      }
    },
    [category, query, limit]
  );

  /*
   * Drop the cursor and start over whenever the category or the query changes —
   * a cursor is only meaningful for the exact filter that produced it. `load` is
   * rebuilt when either changes, so depending on it covers both.
   */
  useEffect(() => {
    setBlogs([]);
    setCursor(null);
    setHasMore(false);
    setAutoLoaded(0);
    load(null, true);
  }, [load]);

  const sentinelRef = useRef(null);
  // The observer callback reads these through refs, so the observer is created
  // once per eligible state instead of being torn down and rebuilt on every
  // cursor change mid-scroll (a rebuild can drop an intersection).
  const loadRef = useRef(load);
  loadRef.current = load;
  const cursorRef = useRef(cursor);
  cursorRef.current = cursor;

  /*
   * Auto-load while there is more to fetch, nothing is in flight, and the reader
   * has not spent the auto budget. This deliberately does NOT depend on `cursor`:
   * it depends on the conditions for observing, and reads the current cursor from
   * a ref at the moment it fires.
   */
  const canAutoLoad = hasMore && !loading && !loadingMore && !error && autoLoaded < AUTO_PAGES;

  useEffect(() => {
    const node = sentinelRef.current;
    // No IntersectionObserver (older browsers, some prerender environments) means
    // no auto-load, which is why the button below is kept as a real fallback.
    if (!node || !canAutoLoad || typeof IntersectionObserver === 'undefined') return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        // Stop observing before fetching: otherwise the sentinel can still be on
        // screen as the page appends and fire a second request for the same cursor.
        observer.disconnect();
        setAutoLoaded((n) => n + 1);
        loadRef.current(cursorRef.current, false);
      },
      // Start fetching a screen early so the next cards are usually already there
      // by the time the reader reaches the end of the current ones.
      { rootMargin: '600px 0px' }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [canAutoLoad]);

  const loadMore = () => load(cursor, false);

  if (loading) {
    return (
      <div className="blog-grid" aria-busy="true" aria-label="Loading articles">
        {Array.from({ length: 6 }, (_, i) => (
          <CardSkeleton key={i} />
        ))}
      </div>
    );
  }

  // Only a first-page failure blanks the list. A failed later page is reported
  // below the cards that already loaded.
  if (error && !blogs.length) {
    return (
      <div className="state-msg state-error" role="alert">
        <p>{error}</p>
        <button type="button" className="btn btn-ghost" onClick={() => load(null, true)}>
          Try again
        </button>
      </div>
    );
  }

  /*
   * A page can come back empty while hasMore is true: the server applies the
   * search filter after reading its window, so an early window may contain no
   * matches. Offer to keep looking rather than claiming there are no results.
   */
  if (!blogs.length) {
    return (
      <div className="state-msg">
        <p>{hasMore ? `No matches in the first ${limit} articles.` : emptyMessage}</p>
        {hasMore && (
          <button
            type="button"
            className="btn btn-primary"
            disabled={loadingMore}
            onClick={loadMore}
          >
            {loadingMore ? 'Searching…' : 'Keep searching'}
          </button>
        )}
      </div>
    );
  }

  return (
    <>
      {/* No total is available from a cursor-paged API, so this counts what has
          actually been loaded rather than claiming "X of Y". */}
      <p className="result-count" aria-live="polite">
        {blogs.length} article{blogs.length === 1 ? '' : 's'}
        {hasMore ? ' so far' : ''}
        {query ? ` matching “${query}”` : ''}
      </p>

      <div className="blog-grid">
        {blogs.map((b, i) => (
          <BlogCard key={b.blogId} blog={b} headingLevel="h2" priority={i === 0} />
        ))}
      </div>

      {/* hasMore is the ONLY end-of-list signal. Never infer the end from a page
          being shorter than `limit`. */}
      {hasMore && (
        <>
          {/* Zero-height marker the observer watches. It sits above the loading
              skeletons so the rootMargin lead time is measured from the end of
              the real cards. */}
          <div ref={sentinelRef} aria-hidden="true" className="scroll-sentinel" />

          {loadingMore && (
            <div className="blog-grid blog-grid-more" aria-hidden="true">
              {Array.from({ length: 3 }, (_, i) => (
                <CardSkeleton key={i} />
              ))}
            </div>
          )}

          {error && !loadingMore && (
            <div className="load-more-wrap load-more-error" role="alert">
              <p className="result-count">{error}</p>
              <button type="button" className="btn btn-ghost" onClick={loadMore}>
                Try again
              </button>
            </div>
          )}

          {/* Appears once auto-loading has spent its budget, and stands in
              permanently where IntersectionObserver does not exist. */}
          {!loadingMore && !error && !canAutoLoad && (
            <div className="load-more-wrap">
              <button type="button" className="btn btn-primary" onClick={loadMore}>
                Load more articles
              </button>
            </div>
          )}
        </>
      )}

      {/* Worth saying only when the reader actually scrolled through pages. */}
      {!hasMore && !loadingMore && blogs.length > limit && (
        <p className="list-end" role="status">
          You have reached the end.
        </p>
      )}
    </>
  );
}

export default BlogList;
